import { useEffect, useState } from 'react';

/**
 * Makes a scroll container respond to the mouse wheel / trackpad when it lives in
 * portalled Popover content opened from inside a Radix modal Dialog or Sheet.
 *
 * The Dialog mounts a react-remove-scroll lock that preventDefault()s wheel events
 * whose target isn't inside DialogContent (a DOM contains() check). Portalled
 * content always fails it, so the wheel is dead while scrollbar drag still works.
 * We cancel the event and scroll manually from a NATIVE non-passive listener —
 * React's onWheel is passive, so its preventDefault() is ignored. See PR #748,
 * docs/reference/ui-behavior-oracle.md.
 *
 * Returns a callback ref. It is state-backed on purpose: a portalled node mounts
 * after the opener's effects run, so a plain useRef + useEffect attaches nothing.
 */
export function useWheelScroll<T extends HTMLElement = HTMLDivElement>() {
  const [el, setEl] = useState<T | null>(null);
  useEffect(() => {
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Firefox reports deltas in lines (1) or pages (2) rather than pixels (0).
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      el.scrollTop += e.deltaY * factor;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [el]);
  return setEl;
}
